import { Injectable } from '@angular/core';
import { AnalyticsGraphSettings } from '@services/analytics-services/core/models/analytics-graph-settings';
import {
  AnalyticsInfo,
  AnalyticsInterval,
  AnalyticsQueryContext,
  FormatterType,
} from '@services/analytics-services/core/models';
import {
  formatAnalyticsDate,
  getChangePercentage,
  getStatsSum,
} from '@services/analytics-services/core/analytics.utils';
import { isGreaterThanYear } from '@services/core-services/date-range.utils';
import { COMPARE_COLORS, DEFAULT_CHART_OPTIONS, DEFAULT_DATASET_OPTIONS } from './constant';

@Injectable()
export class AnalyticsGraphService {
  constructor() {}

  /**
   * Get chart settings for the analytics
   * @param info
   * @param key
   * @param title
   * @param formatter
   */
  getChartSettings<T>(
    info: AnalyticsInfo<T>,
    key: keyof T,
    title: string,
    formatter?: FormatterType
  ): AnalyticsGraphSettings<T> {
    const { stats, compare_stats, context } = info;
    const value = getStatsSum(stats, key);
    const change = getChangePercentage(stats, compare_stats, key);
    const gty = isGreaterThanYear({
      start: context?.range?.start!,
      end: context?.range?.end!,
    });

    const labels = stats.map(info =>
      formatAnalyticsDate(info.date, context?.interval as AnalyticsInterval, gty)
    );

    const datasets = [
      this.getDataSetOptions({
        stats,
        formatter,
        title,
        data: stats?.map(info => {
          return info.metrics[key];
        }),
      }),
      this.getCompareDataSetOptions({
        stats: compare_stats,
        formatter,
        data: compare_stats?.map(info => {
          return info.metrics[key];
        }),
      }),
    ];

    const dates = stats.map(info => info.date);

    const series = [
      this.getDataSetOptions({
        stats,
        formatter,
        name: title,
        data: stats?.map((info, index) => {
          return {
            y: info.metrics[key],
            x: new Date(dates[index]).getTime(),
          };
        }),
      }),
      this.getCompareDataSetOptions({
        stats: compare_stats,
        formatter,
        name: title,
        data: compare_stats?.map((info, index) => {
          return {
            y: info.metrics[key],
            x: new Date(dates[index]).getTime(),
          };
        }),
      }),
    ];

    const options = this.getOptions(context!);

    return {
      datasets,
      series,
      change,
      value,
      query: context,
      options,
      labels,
    };
  }

  private getOptions(context: AnalyticsQueryContext) {
    const params = {
      plugins: {},
      ...DEFAULT_CHART_OPTIONS,
    };

    return params;
  }

  private getDataSetOptions(options?: any) {
    return {
      ...DEFAULT_DATASET_OPTIONS,
      ...(options || {}),
    };
  }

  private getCompareDataSetOptions(options: any) {
    return {
      ...DEFAULT_DATASET_OPTIONS,
      borderDash: [2, 2],
      borderColor: COMPARE_COLORS.compare,
      backgroundColor: COMPARE_COLORS.compare,
      pointBorderColor: COMPARE_COLORS.compare,
      pointBackgroundColor: COMPARE_COLORS.compare,
      ...options,
    };
  }
}
